var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TableBody, TableCell, TableRow, Typography, } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { formatNumberByLocale } from '../../../util';
import { FallbackComponent, NumberInput } from '../../common';
import { currencyCatalog } from '../../common/currency/currencyCatalog';
import { WizardTable } from '../common/WizardTable';
var CATALOG_CODES = new Set(currencyCatalog.map(function (c) { return c.id.toUpperCase(); }));
/**
 * A rate row is "custom" (manually priced, no provider quote) when its rate came from a manual
 * save rather than Frankfurter. Provenance is the authoritative signal; catalog membership alone
 * is not, since a catalog code (e.g. one Frankfurter doesn't currently quote for this base) can
 * still be manually rated. Getting this wrong lets a manually-rated code validate as "not custom",
 * which allows a blank rate through and DELETES the org_config_currency_rate row on save.
 */
var isCustomCurrencyRow = function (r) {
    return r.provenance === 'ORG_CONFIG_MANUAL' || !CATALOG_CODES.has(r.currencyCode.toUpperCase());
};
export var CurrencyConversionModal = function (_a) {
    var open = _a.open, rollupCurrency = _a.rollupCurrency, rates = _a.rates, _b = _a.providerRates, providerRates = _b === void 0 ? [] : _b, _c = _a.providerRatesLoading, providerRatesLoading = _c === void 0 ? false : _c, _d = _a.providerRatesError, providerRatesError = _d === void 0 ? false : _d, _e = _a.providerRatesLastUpdated, providerRatesLastUpdated = _e === void 0 ? null : _e, fxSourceLabel = _a.fxSourceLabel, _f = _a.showFxLastUpdated, showFxLastUpdated = _f === void 0 ? true : _f, onClose = _a.onClose, onSave = _a.onSave, _g = _a.readOnly, readOnly = _g === void 0 ? false : _g;
    var _h = useTranslation('orgConfig', { keyPrefix: 'financials' }), t = _h.t, i18n = _h.i18n;
    var _j = useState({}), localRates = _j[0], setLocalRates = _j[1];
    var _k = useState(false), saving = _k[0], setSaving = _k[1];
    var _l = useState({}), rateErrors = _l[0], setRateErrors = _l[1];
    useEffect(function () {
        var _a;
        if (!open)
            return;
        var next = {};
        for (var _i = 0, rates_1 = rates; _i < rates_1.length; _i++) {
            var r = rates_1[_i];
            next[r.currencyCode] = (_a = r.corporateOverrideRate) !== null && _a !== void 0 ? _a : '';
        }
        setLocalRates(next);
        setRateErrors({});
    }, [open, rates]);
    var hasRates = rates.length > 0;
    var providerRatesByCurrency = useMemo(function () { return new Map(providerRates.map(function (r) { return [r.currencyCode.toUpperCase(), r.providerRate]; })); }, [providerRates]);
    var formattedCurrency = useMemo(function () { return (rollupCurrency || 'USD').toUpperCase(); }, [rollupCurrency]);
    var formattedProviderUpdatedAt = useMemo(function () {
        var _a;
        if (!providerRatesLastUpdated)
            return '-';
        return new Intl.DateTimeFormat((_a = i18n === null || i18n === void 0 ? void 0 : i18n.language) !== null && _a !== void 0 ? _a : 'en', { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(providerRatesLastUpdated));
    }, [i18n === null || i18n === void 0 ? void 0 : i18n.language, providerRatesLastUpdated]);
    var rateValidationSchema = useMemo(function () {
        return yup.object(Object.fromEntries(rates.map(function (r) {
            var isCustom = isCustomCurrencyRow(r);
            return [
                r.currencyCode,
                yup.string().test('min-rate', isCustom
                    ? t('conversion.rateRequiredForCustom', {
                        defaultValue: 'A corporate exchange rate is required for custom currencies.',
                    })
                    : t('conversion.rateMinError', { defaultValue: 'Rate must be at least 0.0001' }), function (v) {
                    if (!v || v === '')
                        return !isCustom;
                    return Number.parseFloat(v) >= 0.0001;
                }),
            ];
        })));
    }, [rates, t]);
    var validateRates = function () {
        var _a;
        var _b;
        var errors = {};
        for (var _i = 0, rates_2 = rates; _i < rates_2.length; _i++) {
            var r = rates_2[_i];
            var val = (_b = localRates[r.currencyCode]) !== null && _b !== void 0 ? _b : '';
            try {
                rateValidationSchema.validateSyncAt(r.currencyCode, (_a = {}, _a[r.currencyCode] = val, _a));
            }
            catch (e) {
                errors[r.currencyCode] = e.message;
            }
        }
        setRateErrors(errors);
        return Object.keys(errors).length === 0;
    };
    var handleSave = function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!validateRates())
                        return [2 /*return*/];
                    setSaving(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, , 3, 4]);
                    return [4 /*yield*/, onSave(rates.map(function (r) {
                            var _a;
                            return ({
                                currencyCode: r.currencyCode,
                                corporateOverrideRate: (_a = localRates[r.currencyCode]) !== null && _a !== void 0 ? _a : '',
                            });
                        }))];
                case 2:
                    _a.sent();
                    onClose();
                    return [3 /*break*/, 4];
                case 3:
                    setSaving(false);
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(Dialog, __assign({ open: open, onClose: onClose, maxWidth: "md", fullWidth: true }, { children: [_jsx(DialogTitle, __assign({ variant: "h1" }, { children: t('conversion.modalTitle', { defaultValue: 'Currency Conversion' }) })), _jsx(DialogContent, { children: _jsxs(Stack, __assign({ spacing: 4, sx: { pt: 1 } }, { children: [_jsx(Typography, { children: t('conversion.modalHelp', {
                                defaultValue: 'Enter your corporate exchange rate (if desired) to be used instead of the FX rate.',
                            }) }), _jsxs(Stack, __assign({ direction: "row", justifyContent: "space-between", spacing: 2 }, { children: [_jsx(Typography, __assign({ sx: { fontWeight: 600 } }, { children: fxSourceLabel !== null && fxSourceLabel !== void 0 ? fxSourceLabel : t('conversion.fxSource', { defaultValue: 'Exchange Rate (FX) Source: Frankfurter' }) })), _jsx(Typography, { children: t('conversion.modalCurrency', { defaultValue: 'Currency: {{currency}}', currency: formattedCurrency }) })] })), providerRatesError && (_jsx(Typography, __assign({ color: "error" }, { children: t('conversion.fxLoadFailed', {
                                defaultValue: 'Unable to load Frankfurter rates. Enter corporate rates manually or try again later.',
                            }) }))), hasRates ? (_jsxs(Stack, __assign({ spacing: 2, sx: { position: 'relative' } }, { children: [_jsx(FallbackComponent, __assign({ requesting: providerRatesLoading, overlay: true }, { children: _jsx(WizardTable, __assign({ headers: _jsxs(_Fragment, { children: [_jsx(TableCell, __assign({ sx: { width: 140 } }, { children: t('conversion.currency', { defaultValue: 'Currency' }) })), _jsx(TableCell, __assign({ sx: { width: 180 } }, { children: t('conversion.fxRate', { defaultValue: 'Exchange Rate (FX)' }) })), _jsx(TableCell, __assign({ sx: { width: 180 } }, { children: t('conversion.override', { defaultValue: 'Corporate Exchange Rate' }) }))] }) }, { children: _jsx(TableBody, { children: rates.map(function (r) {
                                                var _a, _b;
                                                return (_jsxs(TableRow, { children: [_jsx(TableCell, __assign({ sx: { color: 'primary.main' } }, { children: r.currencyCode })), _jsx(TableCell, __assign({ sx: { width: 180 } }, { children: providerRatesLoading
                                                                ? '-'
                                                                : (_a = formatNumberByLocale(Number(providerRatesByCurrency.get(r.currencyCode.toUpperCase())))) !== null && _a !== void 0 ? _a : '-' })), _jsx(TableCell, __assign({ sx: { width: 180 } }, { children: _jsx(NumberInput, { fullWidth: true, size: "small", min: 0, max: 999999, decimal: 4, value: (_b = localRates[r.currencyCode]) !== null && _b !== void 0 ? _b : '', disabled: readOnly, error: Boolean(rateErrors[r.currencyCode]), onChange: function (v) {
                                                                    setLocalRates(function (prev) {
                                                                        var _a;
                                                                        return (__assign(__assign({}, prev), (_a = {}, _a[r.currencyCode] = v == null ? '' : String(v), _a)));
                                                                    });
                                                                    setRateErrors(function (prev) {
                                                                        var _a;
                                                                        return (__assign(__assign({}, prev), (_a = {}, _a[r.currencyCode] = '', _a)));
                                                                    });
                                                                } }) }))] }, r.currencyCode));
                                            }) }) })) })), showFxLastUpdated && (_jsx(Typography, __assign({ variant: "body2", color: "text.secondary" }, { children: t('conversion.fxLastUpdated', {
                                        defaultValue: 'Last Updated: {{date}}',
                                        date: formattedProviderUpdatedAt,
                                    }) })))] }))) : (_jsx(Box, __assign({ sx: { py: 2 } }, { children: _jsx(Typography, __assign({ color: "text.secondary" }, { children: t('conversion.noRows', { defaultValue: 'No currency conversion needed.' }) })) })))] })) }), _jsxs(DialogActions, __assign({ sx: { justifyContent: 'space-between' } }, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: onClose, disabled: saving }, { children: t('conversion.close', { defaultValue: 'CLOSE' }) })), _jsx(Button, __assign({ variant: "contained", onClick: handleSave, disabled: saving || !hasRates || readOnly }, { children: t('conversion.save', { defaultValue: 'SAVE' }) }))] }))] })));
};
